import type { FeatureKey, Permission } from "@alm/policy";

export type LensKey = "census" | "safety" | "medPass" | "money" | "growth" | "team";

/** What each lens is drawn from: a person who holds none of these has no dashboard to land on. */
export const LENS_GATES: Record<LensKey, { permission: Permission; feature?: FeatureKey }> = {
  census: { permission: "resident:read", feature: "residents" },
  safety: { permission: "incident:read", feature: "incidents" },
  medPass: { permission: "medication:read", feature: "med-pass" },
  money: { permission: "financial:read" },
  team: { permission: "staff:read" },
  growth: { permission: "crm:read", feature: "crm" },
};

const OPERATOR_ORDER: readonly LensKey[] = [
  "census",
  "safety",
  "medPass",
  "money",
  "growth",
  "team",
];

/* What a person DOES decides their lens: nearly everyone reads residents, but whoever charts doses
 * came for the med pass. */
const FOCUS: ReadonlyArray<readonly [Permission, LensKey]> = [
  ["medication:administer", "medPass"],
  ["incident:manage", "safety"],
  ["financial:manage", "money"],
  ["crm:manage", "growth"],
  ["hr:manage", "team"],
  ["hiring:manage", "team"],
];

/** Visible lenses in this person's order; the first is the hero. Operators get the fixed order. */
export function orderLenses(
  visible: readonly LensKey[],
  can: (permission: Permission) => boolean,
): LensKey[] {
  const shown = OPERATOR_ORDER.filter((key) => visible.includes(key));
  if (can("organization:update")) return shown;
  const focus = FOCUS.filter(([permission, key]) => shown.includes(key) && can(permission)).map(
    ([, key]) => key,
  );
  return [...new Set([...focus, ...shown])];
}
